import { classList, getOffset, isDesktopView, isMobileView, isShowresultPage, qs, storedItemCreate, storedItemDelete, storedItemRead } from '@js/utils/toolbox';
import { ConnectApi } from '@js/plugins/connectApi';
import SearchFieldMulti from './SearchFieldMulti.vue';
import { createVueApp } from '@/src/vue/utils/createVueApp';


export function useSearchFieldMulti (props = {}) {
	const SearchFieldMultiApp = createVueApp(SearchFieldMulti, props);

	return SearchFieldMultiApp;
}

// useSearchFieldMultiCore.js - Main composable for core functionality
export function useSearchFieldMultiCore (props, inputElement, searchFieldMulti, searchFieldMultiWrapper, chipScroller) {
	const maxNumberOfIatas = 6;
	const maxNumberOfIatasTotal = 7;
	const showMissingAirportLink = js_params?.module?.searchFieldMulti1?.infopageMissingAirportEnabled;
	const property = props.bound === 'from' ? 'fromIatas' : 'toIatas';

	// Computed properties
	const isMultipleIataEnabled = computed(() => {
		if (props.multiMode === -1) {
			return props.data.tripType !== 2;
		}

		return props.multiMode === 1;
	});

	const isMaxIatasAdded = computed(() => {
		const isMaxForThisLeg = props.data.legs?.[props.legIndex]?.[property]?.length >= maxNumberOfIatas;
		const isMaxTotal = props.data.legs?.[props.legIndex]?.fromIatas?.length + props.data.legs?.[props.legIndex]?.toIatas?.length >= maxNumberOfIatasTotal;
		return isMaxForThisLeg || isMaxTotal;
	});

	const checkedIatas = computed(() => props.data.legs?.[props.legIndex]?.[property] || []);
	const firstFromIata = computed(() => props.data.legs?.[props.legIndex]?.fromIatas?.[0]?.iata);

	// Input state
	const inputValue = ref('');

	// Dropdown state
	const { scrollIntoView, resetScroll } = useSearchFieldMultiScroll(searchFieldMulti, searchFieldMultiWrapper);
	const isDropdownOpen = ref(false);

	watch(isDropdownOpen, async (newVal) => {
		if (!isDesktopView() || isShowresultPage()) {
			classList(document.body).toggle('modal-open', newVal);
		}

		classList(window?.searchBox?.html).toggle('abSearchBox--show-overlay', newVal);
		// Scroll to top when dropdown opens
		if (newVal) {
			await nextTick();
			scrollIntoView();
			if (searchFieldMultiWrapper.value) {
				searchFieldMultiWrapper.value.scrollTo({ top: 0 });
			}
			document.addEventListener('click', onDocumentClick);
		} else {
			document.removeEventListener('click', onDocumentClick);
		}
	});

	function onDocumentClick (event) {
		if (!searchFieldMulti.value.contains(event.target)) {
			closeDropdown();
		}
		event.stopPropagation();
		event.preventDefault();
	}

	function focusInput () {
		if (inputElement.value) {
			inputElement.value.focus();
		}
	}

	function onInputFocus () {
		isDropdownOpen.value = true;
	}

	function onInputBlur () {
		// isDropdownOpen.value = false;
	}

	function closeDropdown () {
		// Scroll to start of chip scroller
		chipScroller.value.scrollTo({ left: 0 });
		if (!isDropdownOpen.value) {
			return;
		}
		isDropdownOpen.value = false;
		inputValue.value = '';
		if (isMobileView()) {
			inputElement.value.blur();
			resetScroll();
		}
	}

	return {
		// Constants
		showMissingAirportLink,
		property,

		// Refs
		inputElement,
		searchFieldMulti,
		searchFieldMultiWrapper,
		chipScroller,

		// Computed
		isMultipleIataEnabled,
		isMaxIatasAdded,
		checkedIatas,
		firstFromIata,

		// Input state
		inputValue,
		isDropdownOpen,

		// Methods
		focusInput,
		onInputFocus,
		onInputBlur,
		closeDropdown
	};
}

// useIataManagement.js - Manages IATA item addition/removal and localStorage history
export function useIataManagement (coreState, props) {
	const {
		checkedIatas,
		isMaxIatasAdded,
		isMultipleIataEnabled,
		inputValue,
		chipScroller,
		closeDropdown
	} = coreState;

	// Move localStorage logic here
	const iatasHistory = ref(iatasHistoryGet());

	function iatasHistoryGet () {
		return storedItemRead('clickedIataItems') || { from: [], to: [] };
	}

	function iatasHistoryClear () {
		iatasHistory.value = { from: [], to: [] };
		storedItemDelete('clickedIataItems');
	}

	function updateClickedItemsLocalStorage (item) {
		const iataCode = getIataCode(item);
		const isMetro = isMetroItem(item);

		// Prevent duplicates
		const alreadyExists = iatasHistory.value[props.bound].some(_item => getIataCode(_item) === iataCode && isMetroItem(_item) === isMetro);
		if (!alreadyExists) {
			iatasHistory.value[props.bound].push(item);
			storedItemCreate('clickedIataItems', iatasHistory.value);
		}
	}

	function getTemplateKey (item, index) {
		return `${getIataCode(item)}-${index}`;
	}

	function getIataCode (item) {
		return item?.metropolitan_area_iata ?? item?.iata;
	}

	function getDisplayText (item) {
		return item?.metropolitan_area_name ?? item?.airport;
	}

	function isItemAdded (item, isMetro) {
		if (!item) {
			return false;
		}
		const iataCode = getIataCode(item);
		return checkedIatas.value.some(i => i.iata === iataCode && i.isMetro === isMetro);
	}

	function isMetroItem (item) {
		return item?.type === 'metro' || !!item?.children?.length || item?.isMetro === true;
	}

	async function addItemExclusive (item, parentMetro = null) {
		if (isMaxIatasAdded.value) {
			return;
		}

		const isMetro = isMetroItem(item);

		// Check if item is already added
		if (!isItemAdded(item, isMetro)) {
			deleteAllItems();
			await addItem(item, parentMetro);
		}

		closeDropdown();
	}

	async function addItem (item, parentMetro = null) {
		const isMetro = isMetroItem(item);
		if (isMaxIatasAdded.value) {
			return;
		}

		if (isMetro && item.children) {
			item.children.forEach((child) => {
				const childCode = getIataCode(child);
				const idx = checkedIatas.value.findIndex(i => i.iata === childCode && !i.isMetro);
				if (idx !== -1) {
					checkedIatas.value.splice(idx, 1);
				}
			});
		}

		if (!isMultipleIataEnabled.value) {
			checkedIatas.value.length = 0;
		}

		inputValue.value = '';

		if (isItemAdded(item, isMetro)) {
			return;
		}
		if (parentMetro && isItemAdded(parentMetro, true)) {
			return;
		}

		const newLegItem = {
			iata: getIataCode(item),
			text: getDisplayText(item),
			isMetro
		};

		checkedIatas.value.push(newLegItem);
		await nextTick();

		if (chipScroller.value) {
			chipScroller.value.scrollTo({ left: chipScroller.value.scrollWidth });
		}

		updateClickedItemsLocalStorage(parentMetro || item);
	}

	function deleteAllItems (event) {
		// If already empty just close the dropdown
		if (checkedIatas.value.length === 0) {
			coreState.closeDropdown();
			event?.stopPropagation?.();

			return;
		}
		checkedIatas.value.splice(0);
	}

	function removeItem (item, isMetro) {
		const iataCode = getIataCode(item);
		const index = checkedIatas.value.findIndex(i => i.iata === iataCode && i.isMetro === isMetro);
		if (index !== -1) {
			checkedIatas.value.splice(index, 1);
		}
	}

	async function toggleItem (item, parentMetro = null) {
		const isMetro = isMetroItem(item);
		isItemAdded(item, isMetro) ? await removeItem(item, isMetro) : await addItem(item, parentMetro);
		if (coreState.isDropdownOpen.value) {
			coreState.focusInput();
		}
	}

	function toggleIata (item, parentMetro = null) {
		if (parentMetro && !isItemAdded(item, false) && areAllOtherChildrenAdded(parentMetro, item)) {
			parentMetro.children.forEach(child => removeItem(child, false));
			addItem(parentMetro, null);
			return;
		}

		if (parentMetro && isItemAdded(parentMetro, true)) {
			removeItem(parentMetro, true);
			parentMetro.children.forEach((child) => {
				if (child.iata !== item.iata) {
					addItem(child, parentMetro);
				}
			});
			return;
		}

		toggleItem(item, parentMetro);
	}

	function areAllOtherChildrenAdded (metro, excludeItem) {
		return metro.children.every(child => child.iata === excludeItem.iata || isItemAdded(child, false));
	}

	return {
		// IATA helpers
		getTemplateKey,
		getIataCode,
		getDisplayText,
		isItemAdded,
		isMetroItem,
		addItemExclusive,
		addItem,
		deleteAllItems,
		removeItem,
		toggleItem,
		toggleIata,
		areAllOtherChildrenAdded,

		// History state (previously from useLocalStorageHistory)
		iatasHistory,
		iatasHistoryClear
	};
}

// useLocalStorageHistory.js - Manages localStorage history visibility
export function useLocalStorageHistory (coreState, apiState, iataManagement) {
	const { isDropdownOpen, inputValue } = coreState;
	const { isSearchLoading, isSearchResultsVisible, isNoResultsVisible } = apiState;
	const { iatasHistory } = iataManagement;

	const isHistoryVisible = computed(() =>
		(isDropdownOpen.value
			&& !inputValue.value.length
			&& !isSearchLoading.value
			&& iatasHistory.value[coreState.property === 'fromIatas' ? 'from' : 'to']?.length > 0
			&& !isSearchResultsVisible.value
			&& !isNoResultsVisible.value)
		|| (isSearchLoading.value && isHistoryVisible.value)
	);

	return {
		isHistoryVisible
	};
}

// Cache is shared across all instances
const popularAirportsCache = {};
const searchAllCache = {};

export function useApiSearch (coreState, props) {
	const { isDropdownOpen, inputValue, firstFromIata } = coreState;

	const apiResponse = ref({
		airports: [],
		countries: [],
		regions: [],
		nearby_cities: [],
		usedFuzzySearch: false
	});

	let searchAllRequest = null;
	const isSearchLoading = ref(false);

	async function onInputChange (event) {
		const value = event.target.value;
		// Check if multi mode is disabled and there's already one IATA selected
		// If so, clear the selected IATAs
		if (!coreState.isMultipleIataEnabled.value && coreState.checkedIatas.value.length && value.length === 1) {
			coreState.checkedIatas.value.splice(0, coreState.checkedIatas.value.length);
		}
		inputValue.value = value;
		if (searchAllCache[value]) {
			apiResponse.value = searchAllCache[value];
			isDropdownOpen.value = true;
			return;
		}
		if (!value.length) {
			apiResponse.value = {
				airports: [],
				countries: [],
				regions: [],
				nearby_cities: [],
				usedFuzzySearch: false
			};
		}
		if (value.length > 0 && !searchAllCache[value]) {
			isDropdownOpen.value = true;
			searchAllRequest?.abort?.();
			isSearchLoading.value = true;
			searchAllRequest = await ConnectApi('IataAutocomplete', 'searchAll', [value]);
			isSearchLoading.value = false;
			searchAllCache[value] = searchAllRequest;
			apiResponse.value = searchAllRequest;
		}
	}

	// Computed visibility states
	const isSearchResultsVisible = computed(() =>
		(isDropdownOpen.value
			&& (apiResponse.value.airports.length > 0 || apiResponse.value.countries.length > 0 || apiResponse.value.nearby_cities.length > 0))
		|| (isSearchLoading.value && isSearchResultsVisible.value)
	);

	const isNoResultsVisible = computed(() =>
		(isDropdownOpen.value
			&& inputValue.value.length > 0
			&& !isSearchResultsVisible.value
			&& !isSearchLoading.value)
		|| (isSearchLoading.value && isNoResultsVisible.value)
	);

	// Popular airports functionality
	const popularAirports = ref([]);

	const isPopularAirportsVisible = computed(() =>
		(isDropdownOpen.value
			&& popularAirports.value.length > 0
			&& !isSearchResultsVisible.value
			&& !isNoResultsVisible.value)
		|| (isSearchLoading.value && isPopularAirportsVisible.value)
	);

	// Single flat cache keyed by bound + iata (with null normalized)
	const cacheKey = (bound, iata) => `${bound}:${iata ?? 'default'}`;

	watch(firstFromIata, (newVal) => {
		const history = storedItemRead('clickedIataItems') || { from: [], to: [] };
		const bound = props.bound === 'from' ? 'from' : 'to';

		if (history[bound]?.length === 0) {
			fetchPopularAirports(props.bound, newVal);
		}
	}, { immediate: true });

	async function fetchPopularAirports (bound, iata) {
		const apiIata = bound === 'to' ? iata : null;

		const key = cacheKey(bound, apiIata);

		if (popularAirportsCache[key]) {
			popularAirports.value = popularAirportsCache[key];
			return;
		}

		const response = await ConnectApi('IataAutocomplete', 'getMostPopular', [bound, apiIata]);
		const airports = response?.airports || [];

		popularAirports.value = airports;
		popularAirportsCache[key] = airports;
	}

	return {
		apiResponse,
		isSearchLoading,
		onInputChange,
		isSearchResultsVisible,
		isNoResultsVisible,
		popularAirports,
		isPopularAirportsVisible,
		fetchPopularAirports
	};
}

export function useArrowKeyNavigation (coreState, apiState, historyState, iataManagement) {
	const { isDropdownOpen, searchFieldMulti, searchFieldMultiWrapper } = coreState;
	const { apiResponse, popularAirports, isPopularAirportsVisible, isSearchResultsVisible } = apiState;
	const { getIataCode, iatasHistory } = iataManagement;
	const { isHistoryVisible } = historyState;

	const hoveredIndex = ref(0);

	// Display list computation
	const baseDisplayList = computed(() => {
		const sources = [];

		if (isPopularAirportsVisible.value) {
			sources.push(...popularAirports.value);
		}
		if (isSearchResultsVisible.value) {
			sources.push(...apiResponse.value.airports);
		}
		if (isHistoryVisible.value) {
			sources.push(...iatasHistory.value[coreState.property === 'fromIatas' ? 'from' : 'to']);
		}

		return sources.flatMap((item) => {
			return item.children?.length
				? [{ item }, ...item.children.map(child => ({ item: child, parent: item }))]
				: [{ item }];
		});
	});

	const nearbyCitiesFiltered = computed(() => {
		const childCities = apiResponse.value.nearby_cities.flatMap(city => city.children);
		const displayedIataCodes = new Set(baseDisplayList.value.map(item => getIataCode(item.item)));

		return childCities.filter(city => !displayedIataCodes.has(getIataCode(city)));
	});

	const flatDisplayList = computed(() => {
		const sources = [...baseDisplayList.value];

		// Add nearby cities if they exist and aren't duplicates
		if (nearbyCitiesFiltered.value.length) {
			sources.push(...nearbyCitiesFiltered.value.map(city => ({ item: city })));
		}
		return sources;
	});

	function isItemHovered (item) {
		return flatDisplayList.value?.[hoveredIndex.value]?.item === item;
	}

	function setHoveredItem (item) {
		if (isItemHovered(item)) {
			return;
		}
		const index = flatDisplayList.value.findIndex(i => i.item === item);
		if (index === -1) {
			return;
		}
		hoveredIndex.value = index;
	}

	// Watch for search results changes
	watch(() => apiResponse.value.airports, async (newVal) => {
		// Reset index on new search results
		if (newVal.length > 0 && isDropdownOpen.value) {
			hoveredIndex.value = 0;
			await nextTick();
			scrollToHoveredItem();
		}
	});

	// Watch dropdown open state
	watch(isDropdownOpen, (newVal) => {
		if (newVal) {
			hoveredIndex.value = 0;
		}
	});

	async function onArrowKey (direction) {
		if (!isDropdownOpen.value || flatDisplayList.value.length === 0) {
			return;
		}

		if (direction === 'up') {
			hoveredIndex.value = Math.max(0, hoveredIndex.value - 1);
		} else if (direction === 'down') {
			hoveredIndex.value = Math.min(flatDisplayList.value.length - 1, hoveredIndex.value + 1);
		}

		await nextTick();
		scrollToHoveredItem();
	}

	function scrollToHoveredItem () {
		const hoveredEl = qs('.SearchFieldMulti__row--hovered', searchFieldMulti.value);
		if (!hoveredEl) {
			return;
		}

		const topPadding = 5;
		const wrapperTop = searchFieldMultiWrapper.value.offsetTop;
		const elementTop = hoveredEl.offsetTop;

		let scrollOffset = elementTop - wrapperTop - topPadding;

		// If hovering the first item, scroll to top
		if (hoveredIndex.value === 0) {
			scrollOffset = 0;
		}

		searchFieldMultiWrapper.value.scrollTo({
			top: scrollOffset,
			behavior: 'smooth'
		});
	}

	return {
		hoveredIndex,
		baseDisplayList,
		nearbyCitiesFiltered,
		flatDisplayList,
		onArrowKey,
		scrollToHoveredItem,
		isItemHovered,
		setHoveredItem
	};
}

export function useKeyboardHandlers (coreState, navigationState, iataManagement) {
	const { isDropdownOpen, closeDropdown, inputValue, checkedIatas } = coreState;
	const { hoveredIndex, flatDisplayList } = navigationState;
	const { addItemExclusive } = iataManagement;

	function onTab () {
		onEnter();
		closeDropdown();
	}

	function onBackspace (event) {
		if (inputValue.value.length === 0 && checkedIatas.value.length > 0) {
			event.preventDefault();
			checkedIatas.value.splice(checkedIatas.value.length - 1, 1);
		}
	}

	function onEnter () {
		// Add hovered item if: dropdown is open and an item is hovered
		const hoveredItem = flatDisplayList.value?.[hoveredIndex.value];
		if (isDropdownOpen.value && hoveredItem) {
			addItemExclusive(hoveredItem.item, hoveredItem?.parent);
		}
		closeDropdown();
	}

	return {
		onTab,
		onBackspace,
		onEnter
	};
}

export function useGroupExpansion () {
	// holds the key of the currently expanded group, or null
	const activeKey = ref(null);

	// toggle: collapse if already active, otherwise activate
	function toggleGroup (key) {
		activeKey.value = activeKey.value === key ? null : key;
	}

	// helper to check if a given key is active
	function isActive (key) {
		return activeKey.value === key;
	}

	return {
		toggleGroup,
		isActive
	};
}

function useSearchFieldMultiScroll (searchFieldMulti) {
	let initialScrollTop = 0;

	function scrollIntoView () {
		if (!isMobileView()) {
			return;
		}
		initialScrollTop = window.scrollY;

		const elementOffset = getOffset(searchFieldMulti.value);
		setTimeout(() => {
			window.scrollTo({ top: elementOffset.top - 8 });
		}, 1);
	}

	function resetScroll () {
		window.scrollTo({ top: initialScrollTop });
	}

	return {
		scrollIntoView,
		resetScroll
	};
}
